/**
 * File: DuplicateWarning.tsx
 * Path: apps/admin/src/shared/components/DuplicateWarning.tsx
 * Description: A heads-up under the lead form when the phone number,
 * website, email or name typed already belongs to a lead or a client, with
 * a link to it. It never blocks saving: two branches of one business, or a
 * client who is also a new lead, are real. Our own phone and email, and any
 * number two or more records share, are not treated as a match.
 */

import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { MATCH_REASON_LABELS, type MatchDetails } from "@idk/config";
import { useDuplicateWarningController } from "../hooks/useDuplicateWarningController";

export const DuplicateWarning: React.FC<{
  details: MatchDetails;
  /** The lead being edited, so it doesn't match itself. */
  ignoreLeadId?: string;
}> = ({ details, ignoreLeadId }) => {
  const found = useDuplicateWarningController(details, ignoreLeadId);

  if (found.length === 0) return null;

  return (
    <div
      role="status"
      className="space-y-stack-xs border border-tone-amber/60 bg-tone-amber/10 p-stack-sm sm:col-span-2"
    >
      <p className="flex items-center gap-stack-xs text-sm font-semibold text-foreground">
        <AlertTriangle className="h-4 w-4 shrink-0 text-tone-amber" aria-hidden />
        This may already be in the CRM
      </p>
      <ul className="space-y-stack-xs text-sm">
        {found.map((match) => (
          <li key={match.key}>
            <Link to={match.href} className="font-medium text-primary underline-offset-4 hover:underline">
              {match.name}
            </Link>{" "}
            <span className="text-muted-foreground">
              ({match.kind}, {match.reasons.map((r) => MATCH_REASON_LABELS[r]).join(" and ")})
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">You can still save it if it is a different business.</p>
    </div>
  );
};
